import './styles.scss'
import React, { useEffect, useRef, useState, useMemo } from 'react'
import ReactHtmlParser from 'react-html-parser'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import { useTranslation } from 'react-i18next'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { ROUTE_STRINGS, TABLE_CONST } from 'Utils/Constants'
import { getLocalPaymentReportTableColumns, setLocalWithPaymentReportTableColumns } from 'Utils/LocalStorageHandler'
import { commonMethods } from 'Utils/commonMethods'
import { ButtonComponent, DialogModal, ReportColumnSettingsDialog } from 'Components/UIComponents'
import YearMonthNavigation from 'Components/UIComponents/YearMonthNavigation'
import { CoffeeWebLogoPNG, WaterMarkImageForInvoice } from 'Assets/Icons'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import Loader from 'Components/Loader'
import LayoutContainer from 'Components/LayoutComponent'
import { exportReportToExcel } from 'Utils/reportExportUtils'
import { useReportColumnSettings } from 'Utils/reportColumnSettingsUtils'
import ReconcileForm from './ReconcileForm'
import MissingPaymentReport from './MissingPaymentReport'

const defaultPaymentReportColumnsVisibility = {
  idOrder: true,
  invoiceNumber: true,
  subName: true,
  invoiceAmount: true,
  paymentStatus: true,
  paymentSource: true,
  paymentDate: true,
  transactionId: true,
  invoiceusername: true,
  invoiceId: true,
  invoiceDate: true,
  totalAmount: true,
  userId: true,
  orderDate: true,
  paymentFrom: true,
  orderSatus: true,
  email: true,
  phone: true,
  subscriptionId: true,
  subscriptionStartDate: true,
  subscriptionEndDate: true,
  subscriptionDueDays: true,
  subscriptionDuration: true,
  userName: true,
  country: true,
  trackingId: true,
  cgstamount: true,
  igstamount: true,
  sgstamount: true
}

const PaymentReports = ({ setLoading, mockFilterValue, mockShowModal, isLoading }) => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const isMobileScreen = () => window.innerWidth < TABLE_CONST.maxWidthTab

  const columnSettings = useReportColumnSettings({
    getStored: getLocalPaymentReportTableColumns,
    setStored: setLocalWithPaymentReportTableColumns,
    defaultVisibility: defaultPaymentReportColumnsVisibility,
    minVisibleColumns: 4
  })

  const [selectedYearData, setSelectedYearData] = useState(null)
  const [selectedYearIndex, setSelectedYearIndex] = useState(null)
  const [selectedMonthData, setSelectedMonthData] = useState(null)
  const [selectedRowData, setSelectedRowData] = useState(null)
  const [allPaymentReports, setAllPaymentReports] = useState([])
  const [filteredPaymentData, setFilteredPaymentData] = useState([])
  const [selectedSubscriptions, setSelectedSubscriptions] = useState([])
  const [filterValue, setFilterValue] = useState([])
  const [invoiceNumberSearch, setInvoiceNumberSearch] = useState('')
  const [amountSearch, setAmountSearch] = useState('')
  const [selectedPaymentSource, setSelectedPaymentSource] = useState('')
  const [paymentId, setPaymentId] = useState('')
  const [searchedValue, setSearchedValue] = useState('')
  const [selectedValue, setSelectedValue] = useState('')
  const [invoiceContent, setInvoiceContent] = useState('')
  const [dates, setDates] = useState(null)
  const [visible, setVisible] = useState(false)
  const [isFilterApplied, setIsFilterApplied] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [showDialog, setShowDialog] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {} })
  const [downloadReportsModal, setDownloadReportsModal] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onConfirmation: () => {} })
  const [isMissingReportModalOpen, setIsMissingReportModalOpen] = useState(false)
  const [paymentSourceOptions, setPaymentSourceOptions] = useState([])
  const [paymentStatusOptions, setPaymentStatusOptions] = useState([])
  const [subscriptionOptions, setSubscriptionOptions] = useState([])
  const [invoiceNumber, setInvoiceNumber] = useState(null)
  const [monthListData, setMonthListData] = useState([])
  const [filterStates, setFilterStates] = useState({})
  const [selectedFilterValues, setSelectedFilterValues] = useState({})
  const [paymentSourceType, setPaymentSourceType] = useState(null)

  const availableColumns = columnSettings.visibleColumnsMap

  useEffect(() => {
    getSubscriptionName()
    getMonthForPaymentReports()
    columnSettings.initStorageIfEmpty()
    // Testing Purpose
    if (mockFilterValue) {
      setFilterValue(mockFilterValue)
      setShowModal(mockShowModal)
    }
  }, [])

  useEffect(() => {
    applyFilters()
  }, [filterValue, searchedValue, selectedSubscriptions, invoiceNumberSearch, amountSearch, selectedValue, selectedPaymentSource, paymentId, dates])

  useEffect(() => {
    if (!monthListData || monthListData.length === 0) return

    const urlParams = new URLSearchParams(location.search)
    const yearParam = urlParams.get('year')
    const monthParam = urlParams.get('month')

    const yearIndex = monthListData.findIndex((item) => item.year.toString() === yearParam)

    if (yearIndex !== -1) {
      const yearData = monthListData[yearIndex]

      setSelectedYearIndex(yearIndex)
      setSelectedYearData(yearData)

      if (monthParam) {
        const foundMonth = yearData.months.find((m) => m.name.toLowerCase() === monthParam.toLowerCase())

        if (foundMonth) {
          setSelectedMonthData(foundMonth)
          getAllPaymentReportDetails(foundMonth.name, yearData.year)
        } else {
          setSelectedMonthData(null)
          setFilterStates({})
          setDates(null)
          setFilterValue([])
          setSearchedValue('')
          setInvoiceNumberSearch('')
          setPaymentId('')
          setAmountSearch('')
        }
      } else {
        setSelectedMonthData(null)
        setFilterStates({})
        setDates(null)
        setFilterValue([])
        setSearchedValue('')
        setInvoiceNumberSearch('')
        setPaymentId('')
        setAmountSearch('')
      }
    } else {
      setSelectedYearIndex(null)
      setSelectedYearData(null)
      setSelectedMonthData(null)
      setAllPaymentReports([])
      setFilterStates({})
      setDates(null)
      setFilterValue([])
      setSearchedValue('')
      setInvoiceNumberSearch('')
      setPaymentId('')
      setAmountSearch('')
    }
  }, [location.search, monthListData])

  const getAllPaymentReportDetails = async (month, year) => {
    setShowDialog({ ...showDialog, showModal: false })

    setLoading(true)
    try {
      const response = await apiAdapterCoffeeWeb.getPaymentMonthWiseReport(year, month)

      const paymentDateResponse = response?.data?.returnLst
      const modifiedData = paymentDateResponse.map((item) => {
        const paymentDate = commonMethods.utcToLocalTime(item.paymentDate)

        return {
          ...item,
          paymentDate
        }
      })

      setPaymentStatusOptions([...new Set(modifiedData.map((paymentStatus) => paymentStatus.paymentStatus))])
      setPaymentSourceOptions([...new Set(modifiedData.map((paymentSource) => paymentSource.paymentSource))])
      setAllPaymentReports(modifiedData)
      setFilteredPaymentData(modifiedData)
    } catch {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('NO_DATA_FOUND'),
        onRetry: () => getAllPaymentReportDetails(),
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const getMonthForPaymentReports = async () => {
    setShowDialog({ ...showDialog, showModal: false })

    setLoading(true)
    try {
      const response = await apiAdapterCoffeeWeb.getPaymentReportMonths()

      const monthData = response?.data?.returnLst

      setMonthListData(monthData)
    } catch {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('NO_DATA_FOUND'),
        onRetry: () => getMonthForPaymentReports(),
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const getInvoiceNumberContent = async (invoiceNumber, showPreview, paymentSourceType) => {
    setInvoiceNumber(invoiceNumber)
    setPaymentSourceType(paymentSourceType)
    setLoading(true)
    try {
      const response = await apiAdapterCoffeeWeb.getInvoiceNumberContent(invoiceNumber)
      const content = response?.data?.returnLst

      if (content) {
        const tempDiv = document.createElement('div')

        tempDiv.innerHTML = content

        const bannerImage = tempDiv.querySelector('.banner-image')

        if (bannerImage) {
          bannerImage.style.transform = 'rotate(-7deg)'
          bannerImage.style.opacity = '0.1'
        }

        const container = tempDiv.querySelector('.container')

        container.style.width = '90%'
        container.style.margin = '10 auto'
        container.style.backgroundImage = `url(${WaterMarkImageForInvoice})`
        container.style.backgroundSize = '255px'

        const updatedContent = tempDiv.innerHTML.replace(/<img[^>]+src="([^">]+)"/g, (match) => match.replace(/src="[^">]+"/, `src="${CoffeeWebLogoPNG}"`)).replace(/rotate:\s*-?\d+deg;/g, '')

        if (isMobileScreen() || !showPreview) {
          downloadInvoice(updatedContent, invoiceNumber, paymentSourceType)
          setShowModal(showPreview)
        } else {
          setInvoiceContent(updatedContent)
          setShowModal(showPreview)
        }
      }
    } catch (error) {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('INVOICE_GENERATION_FAILED'),
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const getSubscriptionName = async () => {
    try {
      const response = await apiAdapterCoffeeWeb.getSubscriptionNameList()
      const { returnLst } = response.data

      setSubscriptionOptions(returnLst)
    } catch (error) {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN_LATER'),
        onHide: handleDismiss
      })
    }
  }

  const downloadInvoice = async (content, invoiceNumber, paymentSourceType) => {
    const clonedElement = document.createElement('div')
    const isMobile = window.innerWidth <= 768

    clonedElement.style.position = 'absolute'
    clonedElement.style.top = '-9999px'
    clonedElement.style.left = '0'

    if (isMobile) {
      clonedElement.style.width = '100vw'
      clonedElement.style.padding = '0'
      clonedElement.style.margin = '0'
      clonedElement.style.boxSizing = 'border-box'
      clonedElement.style.maxWidth = '100%'
      clonedElement.style.transformOrigin = 'top left'
    }
    clonedElement.innerHTML = content
    document.body.appendChild(clonedElement)

    const images = clonedElement.getElementsByTagName('img')
    const imageLoadPromises = Array.from(images).map(
      (img) =>
        new Promise((resolve) => {
          // eslint-disable-next-line no-param-reassign
          img.onload = resolve
          // eslint-disable-next-line no-param-reassign
          img.onerror = resolve
          if (img.complete) resolve()
        })
    )

    await Promise.all(imageLoadPromises)

    try {
      const scale = isMobile ? 2 : 2.5

      const canvas = await html2canvas(clonedElement, {
        scale,
        width: clonedElement.scrollWidth,
        height: clonedElement.scrollHeight,
        windowWidth: clonedElement.scrollWidth,
        windowHeight: clonedElement.scrollHeight
      })

      const imgData = canvas.toDataURL('image/png')

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })

      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight)
      const formattedPaymentSource = paymentSourceType?.split('-').pop().trim()

      pdf.save(`${invoiceNumber}-${formattedPaymentSource}.pdf`)
    } catch (err) {
      console.error('Error generating PDF:', err)
    } finally {
      document.body.removeChild(clonedElement)
    }
  }

  const exportToExcel = () => {
    const dataToExport = isFilterApplied ? allPaymentReports : filteredPaymentData

    exportReportToExcel(dataToExport || [], columns, (col) => col.dataField && col.dataField !== 'reconcile' && availableColumns[col.dataField], {
      sheetName: 'Payment Reports',
      fileNamePrefix: 'payment_reports',
      getDateFromItem: (item) => (item.invoiceDate ? new Date(item.invoiceDate) : null)
    })

    setDownloadReportsModal({ ...downloadReportsModal, showModal: false })
  }

  const applyFilters = () => {
    let filteredData = [...filteredPaymentData]
    let isAnyFilterApplied = false

    const filterFunctions = [
      {
        condition: () => searchedValue && filterValue.includes('Order Id'),
        filter: (item) => item.idOrder?.toString().toLowerCase().includes(searchedValue.toLowerCase())
      },
      {
        condition: () => invoiceNumberSearch && filterValue.includes('Invoice Number'),
        filter: (item) => (item.invoiceNumber?.toLowerCase() || '').includes(invoiceNumberSearch.toLowerCase())
      },
      {
        condition: () => paymentId && filterValue.includes('Payment ID'),
        filter: (item) => (item.transactionId?.toLowerCase() || '').includes(paymentId.toLowerCase())
      },
      {
        condition: () => filterValue.includes('Subscription Name') && selectedSubscriptions.length,
        filter: ({ subName }) => selectedSubscriptions.includes(subName)
      },
      {
        condition: () => amountSearch && filterValue.includes('Invoice Amount'),
        filter: (item) => (item.invoiceAmount?.toString() || '').includes(amountSearch)
      },
      {
        condition: () => selectedValue,
        filter: ({ paymentStatus }) => paymentStatus.toLowerCase() === selectedValue.toLowerCase()
      },
      {
        condition: () => selectedPaymentSource,
        filter: ({ paymentSource }) => paymentSource.toLowerCase() === selectedPaymentSource.toLowerCase()
      },

      {
        condition: () => dates && dates.length === 2,
        filter: (item) => {
          const paymentDate = commonMethods.parseCustomDateFormat(item.paymentDate)
          const [startDate, endDate] = dates
          const adjustedEndDate = new Date(endDate)

          adjustedEndDate.setHours(23, 59, 59, 999)

          return paymentDate >= startDate && paymentDate <= adjustedEndDate
        }
      }
    ]

    filterFunctions.forEach(({ condition, filter }) => {
      if (condition()) {
        filteredData = filteredData.filter(filter)
        setIsFilterApplied(true)
        isAnyFilterApplied = true
      }
    })

    setAllPaymentReports(filteredData)
    setIsFilterApplied(isAnyFilterApplied)
  }

  const handleYearClick = (item, index) => {
    setSelectedYearIndex(index)
    setSelectedYearData(monthListData[index])

    const updatedParams = new URLSearchParams(location.search)

    updatedParams.set('year', item.year)
    updatedParams.delete('month')

    history.push({
      pathname: ROUTE_STRINGS.paymentReports,
      search: updatedParams.toString()
    })
  }

  const handleMonthClick = (month) => {
    setSelectedMonthData(month)

    const updatedParams = new URLSearchParams(location.search)

    updatedParams.set('month', month.name)

    getAllPaymentReportDetails(month.name, selectedYearData.year)

    history.push({
      pathname: ROUTE_STRINGS.paymentReports,
      search: updatedParams.toString()
    })
  }

  const handleBack = () => {
    setSelectedYearData(null)
    setSelectedYearIndex(null)

    const updatedParams = new URLSearchParams(location.search)

    updatedParams.delete('year')
    updatedParams.delete('month')

    history.push({
      pathname: ROUTE_STRINGS.paymentReports,
      search: updatedParams.toString()
    })
  }

  const handleNext = () => {
    if (selectedYearIndex < monthListData.length - 1) {
      const newIndex = selectedYearIndex + 1

      setSelectedYearIndex(newIndex)
      setSelectedYearData(monthListData[newIndex])

      const updatedParams = new URLSearchParams(location.search)

      updatedParams.set('year', monthListData[newIndex].year)
      updatedParams.delete('month')

      history.push({
        pathname: ROUTE_STRINGS.paymentReports,
        search: updatedParams.toString()
      })
    }
  }

  const handlePrevious = () => {
    if (selectedYearIndex > 0) {
      const newIndex = selectedYearIndex - 1

      setSelectedYearIndex(newIndex)
      setSelectedYearData(monthListData[newIndex])

      const updatedParams = new URLSearchParams(location.search)

      updatedParams.set('year', monthListData[newIndex].year)
      updatedParams.delete('month')

      history.push({
        pathname: ROUTE_STRINGS.paymentReports,
        search: updatedParams.toString()
      })
    }
  }

  const handleMonthBack = () => {
    setSelectedMonthData(null)
    setAllPaymentReports([])
    setFilterStates({})
    setDates(null)
    setFilterValue([])
    setSearchedValue('')
    setInvoiceNumberSearch('')
    setPaymentId('')
    setAmountSearch('')
    const updatedParams = new URLSearchParams(location.search)

    updatedParams.delete('month')

    history.push({
      pathname: ROUTE_STRINGS.paymentReports,
      search: updatedParams.toString()
    })
  }

  const handleSearch = (field, value) => {
    setFilterStates((prev) => ({
      ...prev,
      [field]: { type: 'text', value }
    }))

    if (field === 'Order Id') {
      setSearchedValue(value)
    }

    if (field === 'Invoice Number') {
      setInvoiceNumberSearch(value)
    }
    if (field === 'Payment ID') {
      setPaymentId(value)
    }
    if (field === 'Invoice Amount') {
      setAmountSearch(value)
    }
  }

  const handleDropdownFilterChange = (field, value) => {
    setSelectedFilterValues((prev) => ({
      ...prev,
      [field]: value
    }))

    if (field === 'Subscription Name') {
      setSelectedSubscriptions(value)
    }
    if (field === 'Payment Status') {
      setSelectedValue(value)
    }
    if (field === 'Payment Source') {
      setSelectedPaymentSource(value)
    }
  }

  const handleDateRangeChange = (field, value) => {
    setFilterStates((prev) => ({
      ...prev,
      [field]: {
        type: 'calendar',
        value
      }
    }))

    setDates(value)
  }

  const handleDismiss = () => {
    setShowDialog({ ...showDialog, showModal: false })
  }

  const handleDismissForDownloadReports = () => {
    setDownloadReportsModal({ ...downloadReportsModal, showModal: false })
  }

  const handleChange = (e) => {
    setFilterValue(e.value)
  }

  const handleDownloadReport = () => {
    if (isFilterApplied) setDownloadReportsModal({ showModal: true, modalType: 'info', message: t('DO_YOU_WANT_TO_DOWNLOAD_CURRENT_REPORTS'), onHide: handleDismissForDownloadReports, onConfirmation: exportToExcel })
    else exportToExcel()
  }

  const handleAction = (rowData) => (
    <div className="action-column">
      <Button type="button" icon="pi pi-book reconcile-icon" className="reconcile-btn" tooltip={t('TOOLTIP_RECONCILE')} onClick={() => handleClick(rowData)} tooltipOptions={{ position: 'top' }}></Button>
      <Button type="button" icon="pi pi-download download-icon " className="download-btn" tooltip={t('TOOLTIP_DOWNLOAD')} onClick={() => getInvoiceNumberContent(rowData.invoiceNumber, false, rowData.paymentSource)} tooltipOptions={{ position: 'top' }}></Button>
    </div>
  )

  const handleClick = (item) => {
    setSelectedRowData(item)
    setVisible(true)
  }

  const handleReconcileSuccess = (reconciledRowId) => {
    setAllPaymentReports((prevData) => prevData.filter((item) => item.idOrder !== reconciledRowId))
    setFilteredPaymentData((prevData) => prevData.filter((item) => item.idOrder !== reconciledRowId))
  }

  const clearFilter = (filter) => {
    setFilterValue((prev) => prev.filter((item) => item !== filter))

    const actions = {
      'Order Id': () => setSearchedValue(''),
      'Invoice Number': () => setInvoiceNumberSearch(''),
      'Subscription Name': () => setSelectedSubscriptions([]),
      'Invoice Amount': () => setAmountSearch(''),
      'Payment Status': () => setSelectedValue(null),
      'Payment Source': () => setSelectedPaymentSource(null),
      'Payment ID': () => setPaymentId(''),
      'Payment Date': () => setDates(null)
    }

    actions[filter]?.()

    setSelectedFilterValues((prev) => ({
      ...prev,
      [filter]: null
    }))
  }

  const invoiceNumberBody = (rowData) => (
    <span onClick={() => getInvoiceNumberContent(rowData.invoiceNumber, true, rowData.paymentSource)} className="invoice-number-clickable">
      {rowData.invoiceNumber}
    </span>
  )

  const columns = [
    { dataField: 'idOrder', text: t('ORDER_ID'), className: 'order-id-column' },
    {
      dataField: 'invoiceNumber',
      text: t('INVOICE_NUMBER'),
      className: 'invoice-number-column',
      body: invoiceNumberBody
    },
    { dataField: 'invoiceusername', text: t('INVOICE_USER_NAME'), className: 'invoice-username-column' },
    { dataField: 'invoiceId', text: t('INVOICE_ID'), className: 'invoice-id-column' },
    { dataField: 'invoiceDate', text: t('INVOICE_DATE'), className: 'invoice-date-column' },
    {
      dataField: 'subName',
      text: t('SUBSCRIPTION_ID'),
      className: 'subscription-name-column'
    },
    { dataField: 'subscriptionId', text: t('SUBSCRIPTION_ID_NUMBER'), className: 'subscription-id-column' },
    {
      dataField: 'invoiceAmount',
      text: t('INVOICE_AMOUNT'),
      className: 'total-amount-column'
    },
    { dataField: 'totalAmount', text: t('TOTAL_AMOUNT'), className: 'total-amount-column' },
    {
      dataField: 'paymentStatus',
      text: t('PAYMENT_STATUS'),
      className: 'payment-status-column'
    },
    {
      dataField: 'paymentSource',
      text: t('PAYMENT_SOURCE'),
      className: 'payment-source-column'
    },
    {
      dataField: 'paymentDate',
      text: t('PAYMENT_DATE'),
      className: 'payment-date-column'
    },
    { dataField: 'paymentFrom', text: t('PAYMENT_FROM'), className: 'payment-from-column' },
    {
      dataField: 'transactionId',
      text: t('PAYMENT_ID'),
      className: 'payment-id-column'
    },
    { dataField: 'userId', text: t('USER_ID'), className: 'user-id-column' },
    { dataField: 'orderDate', text: t('ORDER_DATE'), className: 'order-date-column' },
    { dataField: 'orderSatus', text: t('ORDER_STATUS'), className: 'order-status-column' },
    { dataField: 'email', text: t('EMAIL'), className: 'email-column' },
    { dataField: 'phone', text: t('PHONE'), className: 'phone-column' },
    { dataField: 'subscriptionStartDate', text: t('SUBSCRIPTION_START_DATE'), className: 'subscription-start-date-column' },
    { dataField: 'subscriptionEndDate', text: t('SUBSCRIPTION_END_DATE'), className: 'subscription-end-date-column' },
    { dataField: 'subscriptionDueDays', text: t('SUBSCRIPTION_DUE_DAYS'), className: 'subscription-due-days-column' },
    { dataField: 'subscriptionDuration', text: t('SUBSCRIPTION_DURATION'), className: 'subscription-duration-column' },
    { dataField: 'userName', text: t('USER_NAME'), className: 'user-name-column' },
    { dataField: 'country', text: t('COUNTRY'), className: 'country-column' },
    { dataField: 'trackingId', text: t('TRACKING_ID'), className: 'tracking-id-column' },
    { dataField: 'cgstamount', text: t('CGST_AMOUNT'), className: 'cgst-amount-column' },
    { dataField: 'igstamount', text: t('IGST_AMOUNT'), className: 'igst-amount-column' },
    { dataField: 'sgstamount', text: t('SGST_AMOUNT'), className: 'sgst-amount-column' },
    { dataField: 'reconcile', body: handleAction, text: t('RECONCILE'), className: 'reconcile-column' }
  ]

  const columnTypeMap = {
    [t('ORDER_ID')]: 'text',
    [t('INVOICE_NUMBER')]: 'text',
    [t('SUBSCRIPTION_ID')]: 'dropdown',
    [t('INVOICE_AMOUNT')]: 'text',
    [t('PAYMENT_STATUS')]: 'dropdown',
    [t('PAYMENT_SOURCE')]: 'dropdown',
    [t('PAYMENT_ID')]: 'text',
    [t('PAYMENT_DATE')]: 'calendar'
  }

  const filterableDataFields = ['idOrder', 'invoiceNumber', 'transactionId', 'subName', 'invoiceAmount', 'paymentStatus', 'paymentSource', 'paymentDate']
  const dropdownOptions = columns
    .filter((column) => column.dataField && column.dataField !== 'reconcile' && filterableDataFields.includes(column.dataField))
    .map((column) => ({
      value: column.text,
      label: column.text,
      type: columnTypeMap[column.text] || 'text'
    }))

  const filterOptionsMap = {
    [t('SUBSCRIPTION_ID')]: subscriptionOptions,
    [t('PAYMENT_STATUS')]: paymentStatusOptions,
    [t('PAYMENT_SOURCE')]: paymentSourceOptions
  }

  const handleYearChange = (e) => {
    const selectedYear = e

    if (!monthListData || monthListData?.length === 0) return

    const index = monthListData.findIndex((item) => item?.year === selectedYear)

    if (index !== -1) {
      handleYearClick(monthListData[index], index)
    }
  }

  const yearDropdownOptions = monthListData?.map((item) => ({
    label: item.year,
    value: item.year
  }))

  const headerElement = (
    <div className="invoice-header-container">
      <span className="font-bold white-space-nowrap">{t('INVOICE')}</span>
      <div className="invoice-button">
        <ButtonComponent type="Custom" label={t('DOWNLOAD_INVOICE')} variant="action" icon="pi pi-arrow-down" onClick={() => downloadInvoice(invoiceContent, invoiceNumber, paymentSourceType)} />
      </div>
    </div>
  )

  const minMaxDates = useMemo(() => {
    if (!selectedMonthData || !selectedYearData) return { min: null, max: null }

    const monthIndex = new Date(`${selectedMonthData.name} 1, ${selectedYearData.year}`).getMonth()
    const min = new Date(selectedYearData?.year, monthIndex, 1)
    const max = new Date(selectedYearData?.year, monthIndex + 1, 0)

    return { min, max }
  }, [selectedMonthData, selectedYearData])

  return (
    <div className="payment-report-container">
      <LayoutContainer title={'PAYMENT_REPORTS'} breadCrumbsTitle={'paymentReport'} showPaymentDownloadButton={!!(selectedYearData && selectedMonthData)} setDisplayButton={handleDownloadReport} handleModalOpen={() => setIsMissingReportModalOpen(true)} showSettingsButton={!!(selectedYearData && selectedMonthData)} setVisible={columnSettings.openSettings}>
        <YearMonthNavigation
          availableReportYears={monthListData}
          availableReportMonths={selectedYearData}
          onYearFolderClick={handleYearClick}
          onMonthFolderClick={handleMonthClick}
          activeYearIndex={selectedYearIndex}
          onYearDecrement={handlePrevious}
          onYearIncrement={handleNext}
          navigateToYearPage={handleBack}
          availableYearsList={yearDropdownOptions}
          yearDropdownLabelKey="value"
          yearDropdownValueKey="value"
          yearDropdownValue={selectedYearData?.year}
          handleYearDropdownChange={handleYearChange}
          reportTableColumns={columns.filter((column) => column.dataField === 'reconcile' || availableColumns[column.dataField])}
          reportTableData={allPaymentReports}
          tableClassName="payment-report-table"
          loading={isLoading}
          activeYear={selectedYearData?.year}
          activeMonth={selectedMonthData?.name}
          navigateToMonthPage={handleMonthBack}
          dropdownFilterOptions={dropdownOptions}
          multiSelectFilterValues={filterValue}
          onMultiSelectFilterChange={handleChange}
          searchFieldValue={filterStates}
          onSearchInputChange={handleSearch}
          handleClearFilters={clearFilter}
          maximumDate={minMaxDates.max}
          minimumDate={minMaxDates.min}
          selectedDateRangeValue={dates}
          onDateRangeChange={handleDateRangeChange}
          selectedFilterItems={selectedFilterValues}
          onFilterDropdownChange={handleDropdownFilterChange}
          availableFilterOptionsMap={filterOptionsMap}
        />
      </LayoutContainer>
      {selectedRowData && <ReconcileForm visible={visible} setVisible={setVisible} selectedRowData={selectedRowData} onReconcileSuccess={handleReconcileSuccess} />}
      <MissingPaymentReport setVisible={setIsMissingReportModalOpen} visible={isMissingReportModalOpen} subscriptionOptions={subscriptionOptions} />
      <Dialog header={headerElement} visible={showModal} className="invoice-dialog-modal" onHide={() => setShowModal(false)}>
        <div data-testid="invoice-content">{ReactHtmlParser(invoiceContent)}</div>
      </Dialog>
      <DialogModal {...showDialog} onDismiss={handleDismiss} />
      <DialogModal {...downloadReportsModal} onDismiss={handleDismissForDownloadReports} />
      <ReportColumnSettingsDialog visible={columnSettings.isSettingsOpen} onHide={columnSettings.handleSettingsClose} columns={columns} activeCheckBox={columnSettings.activeCheckBox} onCheckboxChange={columnSettings.handleCheckboxChange} onSubmit={columnSettings.handleSettingsSubmit} disableSubmit={columnSettings.disableSubmit} messagesRef={columnSettings.messagesRef} columnsFilter={(col) => col.dataField !== 'reconcile'} />
    </div>
  )
}

export default Loader(PaymentReports)
